import React from 'react';
import classNames from 'classnames';
import { l } from '@locale/setupI18n';
import {
  OpmFirstLastWaterfallDictionaryDto,
  FirstLastSummaryIndexDictionaryDto,
  OpmWaterfallConditionDto,
} from '@app/shared/models/contracts/opm-calculation-results-dto';
import { Operator, TargetMetric } from '@app/shared/models/contracts/enums/shared-enums';
import { enumKeyByValue } from '@app/shared/helpers/enum-key-by-value';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { amountRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import { numberValueFormatter } from '@app/shared/formatters/number-value-formatter';
import styles from '@app/modules/projects/outputs/opm-summary-waterfall/opm-summary-waterfall-erf.module.scss';
import { simulationSectionsDefinition } from '../../helper/FirstLastHelpers';

interface TrancheLevelToeConditionRowsProps {
  conditions: OpmWaterfallConditionDto[];
  opmWaterfall: OpmFirstLastWaterfallDictionaryDto;
  selectedYear: string;
  waterfallIterations: number;
  instrumentKey: string;
  currentTrancheIndex: number;
}

export const TrancheLevelToeConditionRows: React.FC<TrancheLevelToeConditionRowsProps> = ({
  conditions,
  opmWaterfall,
  selectedYear,
  waterfallIterations,
  instrumentKey,
  currentTrancheIndex,
}) => {
  const toeConditions = conditions.filter(
    (c) => c.metric === enumKeyByValue(TargetMetric, TargetMetric.TotalOrdinaryEquity)
  );

  if (toeConditions.length === 0) {
    return null;
  }

  const renderSimulationCells = (
    valueSelector: (
      section: FirstLastSummaryIndexDictionaryDto,
      key: string
    ) => number | null | undefined
  ) =>
    simulationSectionsDefinition.map((section, outerIndex) =>
      Object.entries(opmWaterfall[selectedYear][section]).map(([key, _], innerIndex) => {
        const sectionData = opmWaterfall[selectedYear][
          section
        ] as FirstLastSummaryIndexDictionaryDto;
        const value = valueSelector(sectionData, key);
        return (
          <td
            key={`${section}-${key}`}
            className={classNames({
              [styles['border-right']]: outerIndex === 0 && innerIndex + 1 === waterfallIterations,
            })}>
            <CellValue value={value} {...amountRules} />
          </td>
        );
      })
    );

  const getTranche = (section: FirstLastSummaryIndexDictionaryDto, key: string) => {
    const trancheNames = Object.keys(section[key].instruments[instrumentKey].tranches);
    const trancheName = trancheNames[currentTrancheIndex];
    return section[key].instruments[instrumentKey].tranches[trancheName];
  };

  const hasAnyBuybacks = simulationSectionsDefinition.some((section) =>
    Object.keys(opmWaterfall[selectedYear][section]).some((key) => {
      const sectionData = opmWaterfall[selectedYear][section] as FirstLastSummaryIndexDictionaryDto;
      return getTranche(sectionData, key).ordinaryEquityMetrics?.conditions.some(
        (condition) => (condition.toeHurdleDetails?.cumulativeRedemptions ?? 0) !== 0
      );
    })
  );

  const hasAnyDividends = simulationSectionsDefinition.some((section) =>
    Object.keys(opmWaterfall[selectedYear][section]).some((key) => {
      const sectionData = opmWaterfall[selectedYear][section] as FirstLastSummaryIndexDictionaryDto;
      return getTranche(sectionData, key).ordinaryEquityMetrics?.conditions.some(
        (condition) => (condition.toeHurdleDetails?.cumulativeDividends ?? 0) !== 0
      );
    })
  );

  const hasDeductions = hasAnyBuybacks || hasAnyDividends;

  return (
    <>
      {toeConditions.map((condition, conditionIndex) => {
        const formattedValue = numberValueFormatter({
          value: Number(condition.testValue),
          ...amountRules,
        });
        const operatorSymbol = Operator[condition.operator as keyof typeof Operator];
        const grossLabel = l('_ToeConditionPlaceholders', {
          operator: operatorSymbol,
          value: formattedValue,
          interpolation: { escapeValue: false },
        });
        const netLabel = l('_NetOeHurdleConditionPlaceholders', {
          operator: operatorSymbol,
          value: formattedValue,
          interpolation: { escapeValue: false },
        });

        return (
          <React.Fragment key={conditionIndex}>
            <tr>
              <td
                className={classNames(styles['border-right'])}
                data-testid={`toe-condition-${conditionIndex}-instrument-title`}>
                {grossLabel}
              </td>
              {renderSimulationCells(
                (section, key) =>
                  getTranche(section, key).ordinaryEquityMetrics?.conditions[conditionIndex]
                    ?.toeHurdleDetails?.initialHurdle
              )}
            </tr>

            {hasAnyBuybacks && (
              <tr>
                <td
                  className={classNames(styles['border-right'])}
                  data-testid={`toe-less-buybacks-${conditionIndex}-instrument-title`}>
                  {l('_LessCumulativeBuybacks')}
                </td>
                {renderSimulationCells(
                  (section, key) =>
                    getTranche(section, key).ordinaryEquityMetrics?.conditions[conditionIndex]
                      ?.toeHurdleDetails?.cumulativeRedemptions
                )}
              </tr>
            )}
            {hasAnyDividends && (
              <tr>
                <td
                  className={classNames(styles['border-right'])}
                  data-testid={`toe-less-dividends-${conditionIndex}-instrument-title`}>
                  {l('_LessCumulativeDividends')}
                </td>
                {renderSimulationCells(
                  (section, key) =>
                    getTranche(section, key).ordinaryEquityMetrics?.conditions[conditionIndex]
                      ?.toeHurdleDetails?.cumulativeDividends
                )}
              </tr>
            )}
            {hasDeductions && (
              <tr>
                <td
                  className={classNames(styles['border-right'], styles['bold-text'])}
                  data-testid={`toe-net-condition-${conditionIndex}-instrument-title`}>
                  {netLabel}
                </td>
                {renderSimulationCells(
                  (section, key) =>
                    getTranche(section, key).ordinaryEquityMetrics?.conditions[conditionIndex]
                      ?.toeHurdleDetails?.finalHurdle
                )}
              </tr>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};
